import {
  BUILT_IN_REPAIR_SUBCATEGORIES,
  DEFAULT_SUBCATEGORY_TO_CATEGORY_KEY,
  Expense,
  ExpenseSubcategoryDoc,
  OTHER_REPAIR_CATEGORY_KEY,
} from '@otw/models'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'

const toSubcategoryKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

const parseDate = (raw: unknown): Date => {
  if (!raw) return new Date()
  if (typeof raw === 'object' && raw !== null && 'toDate' in raw) {
    const tdate = (raw as { toDate?: () => Date }).toDate
    if (typeof tdate === 'function') return tdate.call(raw)
  }
  return new Date(raw as string)
}

const FIRESTORE_BATCH_LIMIT = 400

/**
 * Rewrites `oldLabel` -> `newLabel` (or removes when newLabel is null) across:
 *  - Expense.repairSubcategories (array<string>)
 *  - Expense.lineItems[].subcategory (string)
 *
 * Audit row written to `aiExtractionsLog` for traceability.
 */
const rewriteSubcategoryLabel = async (
  oldLabel: string,
  newLabel: string | null,
  actor: string,
): Promise<{ touched: number }> => {
  const expensesCollection = collection(db, 'expenses')
  const snapshot = await getDocs(
    query(expensesCollection, where('repairSubcategories', 'array-contains', oldLabel)),
  )

  let touched = 0
  let batch = writeBatch(db)
  let inBatch = 0

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data() as Pick<Expense, 'repairSubcategories' | 'lineItems'>
    const updates: Record<string, unknown> = {}

    if (Array.isArray(data.repairSubcategories) && data.repairSubcategories.includes(oldLabel)) {
      const next = newLabel
        ? Array.from(new Set(data.repairSubcategories.map(s => (s === oldLabel ? newLabel : s))))
        : data.repairSubcategories.filter(s => s !== oldLabel)
      updates.repairSubcategories = next
    }

    if (Array.isArray(data.lineItems) && data.lineItems.some(li => li?.subcategory === oldLabel)) {
      updates.lineItems = data.lineItems.map(li =>
        li?.subcategory === oldLabel ? { ...li, subcategory: newLabel } : li,
      )
    }

    if (Object.keys(updates).length > 0) {
      batch.update(doc(expensesCollection, docSnap.id), {
        ...updates,
        updated_at: new Date().toISOString(),
        updated_by: actor,
      })
      touched += 1
      inBatch += 1
      if (inBatch >= FIRESTORE_BATCH_LIMIT) {
        await batch.commit()
        batch = writeBatch(db)
        inBatch = 0
      }
    }
  }

  if (inBatch > 0) await batch.commit()

  await addDoc(collection(db, 'aiExtractionsLog'), {
    timestamp: new Date().toISOString(),
    successful: true,
    source: 'taxonomy',
    action: newLabel ? 'rename_subcategory' : 'remove_subcategory',
    oldLabel,
    newLabel,
    touched,
    actor,
  })

  return { touched }
}

/** User-created repair subcategories stored in the `expenseSubcategories` collection. */
export const useExpenseSubcategories = () => {
  const { userInfo } = useAuth()
  const { showError } = useSnackbar()
  const queryClient = useQueryClient()

  const subcategoriesCollection = collection(db, 'expenseSubcategories')

  const { data: subcategories = [], isLoading } = useQuery({
    queryKey: ['expenseSubcategories'],
    queryFn: async (): Promise<ExpenseSubcategoryDoc[]> => {
      let snapshot = await getDocs(subcategoriesCollection)

      // Seed built-ins on first read so the picker has a baseline taxonomy.
      const existingLabels = new Set(
        snapshot.docs.map(d => (d.data().label as string | undefined)?.toLowerCase() ?? ''),
      )
      const missingBuiltIns = BUILT_IN_REPAIR_SUBCATEGORIES.filter(
        label => !existingLabels.has(label.toLowerCase()),
      )
      if (missingBuiltIns.length > 0) {
        await Promise.all(
          missingBuiltIns.map(label => {
            const key = toSubcategoryKey(label)
            return setDoc(doc(subcategoriesCollection, key), {
              label,
              key,
              categoryKey: DEFAULT_SUBCATEGORY_TO_CATEGORY_KEY[label],
              isBuiltIn: true,
              createdBy: 'system',
              createdAt: new Date().toISOString(),
              updatedAt: null,
              updatedBy: null,
            })
          }),
        )
        snapshot = await getDocs(subcategoriesCollection)
      }

      return snapshot.docs
        .map(docSnap => {
          const data = docSnap.data() as Partial<ExpenseSubcategoryDoc>
          return {
            id: docSnap.id,
            label: data.label ?? '',
            key: data.key ?? '',
            categoryKey: data.categoryKey ?? null,
            isBuiltIn: Boolean(data.isBuiltIn),
            createdBy: data.createdBy ?? '',
            createdAt: parseDate(data.createdAt),
            updatedAt: data.updatedAt ? parseDate(data.updatedAt) : null,
            updatedBy: data.updatedBy ?? null,
          } satisfies ExpenseSubcategoryDoc
        })
        .filter(subcategory => subcategory.label.length > 0)
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    staleTime: 1000 * 60 * 5,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['expenseSubcategories'] })
    queryClient.invalidateQueries({ queryKey: ['expenses'] })
  }

  const createSubcategoryMutation = useMutation({
    mutationFn: async (
      input: string | { label: string; categoryKey?: string | null },
    ): Promise<ExpenseSubcategoryDoc> => {
      const label = typeof input === 'string' ? input : input.label
      const categoryKey = typeof input === 'string' ? null : input.categoryKey ?? null
      const trimmed = label.trim()
      if (!trimmed) throw new Error('Subcategory name is required')
      const key = toSubcategoryKey(trimmed)

      const existing = subcategories.find(
        subcategory => subcategory.key === key || subcategory.label.toLowerCase() === trimmed.toLowerCase(),
      )
      if (existing) return existing

      const createdBy = userInfo?.id || userInfo?.email || ''
      const createdAt = new Date()
      await setDoc(doc(subcategoriesCollection, key), {
        label: trimmed,
        key,
        categoryKey,
        isBuiltIn: false,
        createdBy,
        createdAt: createdAt.toISOString(),
        updatedAt: null,
        updatedBy: null,
      })
      return {
        id: key,
        label: trimmed,
        key,
        categoryKey,
        isBuiltIn: false,
        createdBy,
        createdAt,
        updatedAt: null,
        updatedBy: null,
      }
    },
    onSuccess: invalidate,
    onError: (error: Error) => showError(`Error creating subcategory: ${error.message}`),
  })

  const updateSubcategoryMutation = useMutation({
    mutationFn: async ({
      id,
      label,
      categoryKey,
    }: {
      id: string
      label?: string
      categoryKey?: string | null
    }): Promise<{ touched: number }> => {
      const target = subcategories.find(s => s.id === id)
      if (!target) throw new Error('Subcategory not found')

      let touched = 0
      const trimmedLabel = label?.trim()
      const isRename = trimmedLabel !== undefined && trimmedLabel !== target.label
      const isReparent = categoryKey !== undefined && categoryKey !== target.categoryKey
      const newLabel = isRename ? trimmedLabel! : undefined
      const newCategoryKey = isReparent ? categoryKey : undefined

      if (isRename) {
        if (!newLabel) throw new Error('Subcategory name is required')
        if (target.isBuiltIn) throw new Error('Built-in subcategories cannot be renamed')
        const collision = subcategories.find(
          s => s.id !== id && s.label.toLowerCase() === newLabel.toLowerCase(),
        )
        if (collision) throw new Error('A subcategory with that name already exists')
        const actor = (userInfo?.id || userInfo?.email || 'unknown') as string
        const result = await rewriteSubcategoryLabel(target.label, newLabel, actor)
        touched = result.touched
      }

      if (isRename || isReparent) {
        await updateDoc(doc(subcategoriesCollection, id), {
          ...(newLabel !== undefined ? { label: newLabel } : {}),
          ...(newCategoryKey !== undefined ? { categoryKey: newCategoryKey } : {}),
          updatedAt: new Date().toISOString(),
          updatedBy: userInfo?.id || userInfo?.email || '',
        })
      }
      return { touched }
    },
    onSuccess: invalidate,
    onError: (error: Error) => showError(`Error updating subcategory: ${error.message}`),
  })

  const deleteSubcategoryMutation = useMutation({
    mutationFn: async ({
      id,
      replacementLabel,
    }: {
      id: string
      /** Required when the subcategory is in use; ignored when no expenses reference it. */
      replacementLabel?: string | null
    }): Promise<{ touched: number }> => {
      const target = subcategories.find(s => s.id === id)
      if (!target) throw new Error('Subcategory not found')
      if (target.isBuiltIn) throw new Error('Built-in subcategories cannot be deleted')

      const actor = (userInfo?.id || userInfo?.email || 'unknown') as string
      const { touched } = await rewriteSubcategoryLabel(
        target.label,
        replacementLabel ?? null,
        actor,
      )
      await deleteDoc(doc(subcategoriesCollection, id))
      return { touched }
    },
    onSuccess: invalidate,
    onError: (error: Error) => showError(`Error deleting subcategory: ${error.message}`),
  })

  return {
    subcategories,
    subcategoryLabels: subcategories.map(s => s.label),
    isLoading,
    createSubcategory: createSubcategoryMutation.mutateAsync,
    isCreatingSubcategory: createSubcategoryMutation.isPending,
    updateSubcategory: updateSubcategoryMutation.mutateAsync,
    isUpdatingSubcategory: updateSubcategoryMutation.isPending,
    deleteSubcategory: deleteSubcategoryMutation.mutateAsync,
    isDeletingSubcategory: deleteSubcategoryMutation.isPending,
    OTHER_REPAIR_CATEGORY_KEY,
  }
}
